import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationOnIcon,
  LocalShipping as LocalShippingIcon,
  Map as MapIcon
} from '@mui/icons-material';
import { Map, NavigationControl } from '@vis.gl/react-mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { frPostalMap, esPostalMap, itPostalMap, getPostalCodeMapping, countryMappings } from '../../../data/postalCodeMappings';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';

interface PostalCodeRegion {
  id: string;
  name: string;
  postalCodePrefix: string;
  country: string;
  isCountry?: boolean; // Flag to indicate if this is a country-level selection
}

interface InteractiveMapSearchProps {
  open: boolean;
  onClose: () => void;
  onSearch: (loadingRegions: PostalCodeRegion[], deliveryRegions: PostalCodeRegion[]) => void;
}

const InteractiveMapSearch: React.FC<InteractiveMapSearchProps> = ({
  open,
  onClose,
  onSearch
}) => {
  const { t } = useTranslation();

  const [selectedLoading, setSelectedLoading] = useState<PostalCodeRegion[]>([]);
  const [selectedDelivery, setSelectedDelivery] = useState<PostalCodeRegion[]>([]);
  const [mode, setMode] = useState<'loading' | 'delivery'>('loading');
  const [selectionType, setSelectionType] = useState<'regions' | 'countries'>('regions');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapRef, setMapRef] = useState<any>(null);

  const handleRegionClick = (area: PostalCodeRegion) => {
    if (mode === 'loading') {
      const newSelection = selectedLoading.some(a => a.id === area.id)
        ? selectedLoading.filter(a => a.id !== area.id)
        : [...selectedLoading, area];

      setSelectedLoading(newSelection);
    } else {
      const newSelection = selectedDelivery.some(a => a.id === area.id)
        ? selectedDelivery.filter(a => a.id !== area.id)
        : [...selectedDelivery, area];

      setSelectedDelivery(newSelection);
    }
  };

  // Update map layers when selections change
  const updateMapLayers = useCallback((map: any) => {
    if (!map || !map.isStyleLoaded()) return;

    try {
      // Check if layers exist, if not re-add them
      const existingLayers = map.getStyle().layers.map((l: any) => l.id);
      const nutsLayer = map.getStyle().layers.find((l: any) => l.id.includes('nuts'));

      if (!existingLayers.includes('postal-code-labels')) {
        addPostalCodeLabels(map);
      }

      if (!existingLayers.includes('selected-regions-fill') && nutsLayer) {
        addVisualLayers(map, nutsLayer);
      }

      // Update the fill layer if it exists
      if (existingLayers.includes('selected-regions-fill')) {
        const loadingRegions = selectedLoading.filter(r => !r.isCountry);
        const loadingCountries = selectedLoading.filter(r => r.isCountry);
        const deliveryRegions = selectedDelivery.filter(r => !r.isCountry);
        const deliveryCountries = selectedDelivery.filter(r => r.isCountry);

        const loadingRegionNames = loadingRegions.map(r => r.name);
        const loadingCountryCodes = loadingCountries.map(r => r.country);
        const deliveryRegionNames = deliveryRegions.map(r => r.name);
        const deliveryCountryCodes = deliveryCountries.map(r => r.country);

        map.setPaintProperty('selected-regions-fill', 'fill-color', [
          'case',
          // Check if region name matches loading regions
          ['in', ['get', 'NUTS_NAME'], ['literal', loadingRegionNames]],
          'border.blue', // Blue for loading regions
          // Check if country code matches loading countries
          ['in', ['get', 'CNTR_CODE'], ['literal', loadingCountryCodes]],
          'border.blue', // Blue for loading countries
          // Check if region name matches delivery regions
          ['in', ['get', 'NUTS_NAME'], ['literal', deliveryRegionNames]],
          '#4CAF50', // Green for delivery regions (using theme.successGreen value)
          // Check if country code matches delivery countries
          ['in', ['get', 'CNTR_CODE'], ['literal', deliveryCountryCodes]],
          '#4CAF50', // Green for delivery countries (using theme.successGreen value)
          'rgba(0,0,0,0)' // Transparent for unselected
        ]);
      }

      // Update the stroke layer if it exists
      if (existingLayers.includes('selected-regions-stroke')) {
        const loadingRegions = selectedLoading.filter(r => !r.isCountry);
        const loadingCountries = selectedLoading.filter(r => r.isCountry);
        const deliveryRegions = selectedDelivery.filter(r => !r.isCountry);
        const deliveryCountries = selectedDelivery.filter(r => r.isCountry);

        const loadingRegionNames = loadingRegions.map(r => r.name);
        const loadingCountryCodes = loadingCountries.map(r => r.country);
        const deliveryRegionNames = deliveryRegions.map(r => r.name);
        const deliveryCountryCodes = deliveryCountries.map(r => r.country);

        map.setPaintProperty('selected-regions-stroke', 'line-color', [
          'case',
          // Check if region name matches loading regions
          ['in', ['get', 'NUTS_NAME'], ['literal', loadingRegionNames]],
          'border.blue', // Darker blue for loading regions
          // Check if country code matches loading countries
          ['in', ['get', 'CNTR_CODE'], ['literal', loadingCountryCodes]],
          'border.blue', // Darker blue for loading countries
          // Check if region name matches delivery regions
          ['in', ['get', 'NUTS_NAME'], ['literal', deliveryRegionNames]],
          '#388E3C', // Darker green for delivery regions
          // Check if country code matches delivery countries
          ['in', ['get', 'CNTR_CODE'], ['literal', deliveryCountryCodes]],
          '#388E3C', // Darker green for delivery countries
          'rgba(0,0,0,0)' // Transparent for unselected
        ]);
      }
    } catch (_) {
      // Error updating map layers
    }
  }, [selectedLoading, selectedDelivery]);

  // Function to add postal code labels
  const addPostalCodeLabels = (map: any) => {
    try {
      const layers = map.getStyle().layers;
      const nutsLayer = layers.find((l: any) => l.id.includes('nuts'));

      if (nutsLayer) {
        map.addLayer({
          id: 'postal-code-labels',
          type: 'symbol',
          source: nutsLayer.source,
          'source-layer': nutsLayer['source-layer'],
          filter: ['in', ['get', 'CNTR_CODE'], ['literal', ['FR', 'ES', 'IT']]],
          layout: {
            'text-field': [
              'case',
              ['==', ['get', 'CNTR_CODE'], 'FR'],
              [
                'case',
                ['has', ['get', 'NUTS_NAME'], ['literal', frPostalMap]],
                ['get', ['get', 'NUTS_NAME'], ['literal', frPostalMap]],
                ['get', 'NUTS_NAME']
              ],
              ['==', ['get', 'CNTR_CODE'], 'ES'],
              [
                'case',
                ['has', ['get', 'NUTS_NAME'], ['literal', esPostalMap]],
                ['get', ['get', 'NUTS_NAME'], ['literal', esPostalMap]],
                ['get', 'NUTS_NAME']
              ],
              ['==', ['get', 'CNTR_CODE'], 'IT'],
              [
                'case',
                ['has', ['get', 'NUTS_NAME'], ['literal', itPostalMap]],
                ['get', ['get', 'NUTS_NAME'], ['literal', itPostalMap]],
                ['get', 'NUTS_NAME']
              ],
              ['get', 'NUTS_NAME']
            ],
            'text-size': 14,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-optional': true,
            'text-padding': 0,
            'text-max-width': 0,
            'symbol-placement': 'point',
            'symbol-spacing': 50000,
            'text-keep-upright': false,
            'text-rotate': 0,
            'text-transform': 'none',
            'symbol-sort-key': ['get', 'NUTS_ID'],
            'symbol-z-order': 'viewport-y'
          },
          paint: {
            'text-color': '#000000',
            'text-halo-color': 'rgba(0,0,0,0)',
            'text-halo-width': 0
          }
        });
      }
    } catch (_) {
      // Error re-adding postal code labels
    }
  };

  // Function to add visual layers for selected regions
  const addVisualLayers = (map: any, nutsLayer: any) => {
    try {
      // Add selected regions fill layer
      map.addLayer({
        id: 'selected-regions-fill',
        type: 'fill',
        source: nutsLayer.source,
        'source-layer': nutsLayer['source-layer'],
        filter: ['in', ['get', 'CNTR_CODE'], ['literal', ['FR', 'ES', 'IT']]],
        paint: {
          'fill-color': [
            'case',
            ['in', ['get', 'NUTS_NAME'], ['literal', selectedLoading.map(r => r.name)]],
            'border.blue', // Blue for loading
            ['in', ['get', 'NUTS_NAME'], ['literal', selectedDelivery.map(r => r.name)]],
            '#4CAF50', // Green for delivery (using theme.successGreen value)
            'rgba(0,0,0,0)' // Transparent for unselected
          ],
          'fill-opacity': 0.7
        }
      }, 'postal-code-labels'); // Place before postal code labels

      // Add selected regions stroke layer
      map.addLayer({
        id: 'selected-regions-stroke',
        type: 'line',
        source: nutsLayer.source,
        'source-layer': nutsLayer['source-layer'],
        filter: ['in', ['get', 'CNTR_CODE'], ['literal', ['FR', 'ES', 'IT']]],
        paint: {
          'line-color': [
            'case',
            ['in', ['get', 'NUTS_NAME'], ['literal', selectedLoading.map(r => r.name)]],
            'border.blue', // Darker blue for loading
            ['in', ['get', 'NUTS_NAME'], ['literal', selectedDelivery.map(r => r.name)]],
            '#388E3C', // Darker green for delivery
            'rgba(0,0,0,0)' // Transparent for unselected
          ],
          'line-width': 3,
          'line-opacity': 0.8
        }
      }, 'postal-code-labels'); // Place before postal code labels

    } catch (_) {
      // Error adding visual layers
    }
  };


  // Update map layers when selections change
  useEffect(() => {
    if (mapRef && mapLoaded) {
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        updateMapLayers(mapRef);
      });
    }
  }, [selectedLoading, selectedDelivery, mapRef, mapLoaded]);

  // Add global error handler to suppress Mapbox errors
  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;

    // Override console.error to suppress Mapbox errors
    console.error = (...args) => {
      const message = args.join(' ');
      if (message.includes('signal is aborted') || 
          message.includes('AbortError') ||
          message.includes('mapbox-gl') ||
          message.includes('raster-dem') ||
          message.includes('abortTile') ||
          message.includes('_abortTile') ||
          message.includes('_removeTile') ||
          message.includes('updateSources') ||
          message.includes('_render')) {
        return; // Suppress these errors
      }
      originalError.apply(console, args);
    };

    // Override console.warn to suppress Mapbox warnings
    console.warn = (...args) => {
      const message = args.join(' ');
      if (message.includes('signal is aborted') || 
          message.includes('AbortError') ||
          message.includes('mapbox-gl') ||
          message.includes('raster-dem') ||
          message.includes('abortTile') ||
          message.includes('_abortTile') ||
          message.includes('_removeTile') ||
          message.includes('updateSources') ||
          message.includes('_render')) {
        return; // Suppress these warnings
      }
      originalWarn.apply(console, args);
    };

    // Add global error handler for uncaught errors
    const handleError = (event: ErrorEvent) => {
      if (event.message && (
        event.message.includes('signal is aborted') ||
        event.message.includes('AbortError') ||
        event.message.includes('mapbox-gl') ||
        event.message.includes('raster-dem') ||
        event.message.includes('404') ||
        event.message.includes('Not Found') ||
        event.message.includes('mapbox.com')
      )) {
        event.preventDefault();
        event.stopPropagation();
        return false;
      }
    };

    // Add global unhandled rejection handler
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && event.reason.message && (
        event.reason.message.includes('signal is aborted') ||
        event.reason.message.includes('AbortError') ||
        event.reason.message.includes('mapbox-gl') ||
        event.reason.message.includes('raster-dem') ||
        event.reason.message.includes('404') ||
        event.reason.message.includes('Not Found') ||
        event.reason.message.includes('mapbox.com')
      )) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  const handleSearch = () => {
    if (selectedLoading.length === 0 || selectedDelivery.length === 0) {
      toast.warn(t('Please select at least one loading region and one delivery region'));
      return;
    }

    onSearch(selectedLoading, selectedDelivery);
    onClose();
  };

  const handleClear = () => {
    setSelectedLoading([]);
    setSelectedDelivery([]);
  };

  const styles = {
    dialog: {
      '& .MuiDialog-paper': {
        width: '95vw',
        height: '95vh',
        maxWidth: 'none',
        maxHeight: 'none'
      }
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 2,
      borderBottom: '1px solid border.default'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      flex: 1
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      fontWeight: 600
    },
    selectionTypeContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 2
    },
    modeSelectionContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 2
    },
    subtitle: {
      fontWeight: 600
    },
    content: {
      p: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    },
    mapContainer: {
      flex: 1,
      position: 'relative',
      minHeight: 0
    },
    map: {
      width: '100%',
      height: '100%'
    },
    loadingBox: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'background.lightGrey',
      padding: 2,
      borderRadius: 1,
      boxShadow: 1,
      textAlign: 'center',
      zIndex: 1000
    },
    selection: {
      p: 1,
      borderTop: '1px solid border.default',
      bgcolor: 'background.grey',
      maxHeight: '180px',
      overflowY: 'auto'
    },
    selectionTitle: {
      fontWeight: 600,
      mb: 1
    },
    loadingTitle: {
      fontWeight: 500,
      color: 'text.blue',
      mb: 0.5
    },
    deliveryTitle: {
      fontWeight: 500,
      color: 'text.successGreen',
      mb: 0.5
    },
    selectionBox: {
      mb: 1
    },
    actions: {
      p: 2,
      borderTop: '1px solid border.default',
      bgcolor: 'background.white',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    actionsButtons: {
      display: 'flex',
      gap: 1
    },
    chip: {
      m: 0.5,
      fontSize: (theme: any) => theme.typography.fontSizes.small
    },
    modeButton: {
      px: 2,
      py: 1,
      borderRadius: 2,
      fontWeight: 600,
      textTransform: 'none'
    },
    icon: {
      mr: 1,
      fontSize: 18
    }
  };

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <Box sx={styles.header}>
        <Box sx={styles.headerContent}>
          <Box sx={styles.title}>
            <MapIcon />
            <Typography variant="h6">{t('Interactive Map Search')}</Typography>
          </Box>

          {/* Selection Type Toggle */}
          <Box sx={styles.selectionTypeContainer}>
            <Typography variant="subtitle2" sx={styles.subtitle}>
              {t('Selection Type:')}
            </Typography>
            <ToggleButtonGroup
              value={selectionType}
              exclusive
              onChange={(_, newType) => newType && setSelectionType(newType)}
              size="small"
            >
              <ToggleButton value="regions" sx={styles.modeButton}>
                {t('Regions')}
              </ToggleButton>
              <ToggleButton value="countries" sx={styles.modeButton}>
                {t('Countries')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Mode Selection */}
          <Box sx={styles.modeSelectionContainer}>
            <Typography variant="subtitle2" sx={styles.subtitle}>
              {t('Select Mode:')}
            </Typography>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, newMode) => newMode && setMode(newMode)}
              size="small"
            >
              <ToggleButton value="loading" sx={styles.modeButton}>
                <LocalShippingIcon sx={styles.icon} />
                {t('Loading')} {selectionType === 'countries' ? t('Countries') : t('Regions')}
              </ToggleButton>
              <ToggleButton value="delivery" sx={styles.modeButton}>
                <LocationOnIcon sx={styles.icon} />
                {t('Delivery')} {selectionType === 'countries' ? t('Countries') : t('Regions')}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>

        {/* Map */}
        <Box sx={styles.mapContainer}>
          <Map
            key="interactive-map"
            initialViewState={{
              longitude: 5.0,
              latitude: 50.0,
              zoom: 4
            }}
            minZoom={2}
            maxZoom={8}
            maxPitch={85}
            bearing={0}
            style={styles.map}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoibWFwYm94IiwiYSI6ImNpejY4NXVycTA2emYycXBndHRqcmZ3N3gifQ.rJcFIG214AriISLbB6B5aw'}
            mapStyle={process.env.REACT_APP_MAPBOX_STYLE_URL || 'mapbox://styles/mapbox/streets-v12'}
            styleDiffing={false}
            reuseMaps={false}
            antialias={false}
            preserveDrawingBuffer={false}
            failIfMajorPerformanceCaveat={false}
            interactiveLayerIds={['nuts-rg-01m-2021-4326-levl-3-90pj89']}
            onClick={(event: any) => {
              const features = event.features;
              if (features && features.length > 0) {
                const feature = features[0];
                const nutsName = feature.properties?.NUTS_NAME;
                const countryCode = feature.properties?.CNTR_CODE;

                if (selectionType === 'countries') {
                // In countries mode, select the entire country
                  if (countryCode && countryMappings[countryCode]) {
                    const countryRegion: PostalCodeRegion = {
                      id: `country-${countryCode}`,
                      name: countryMappings[countryCode],
                      postalCodePrefix: countryCode,
                      country: countryCode,
                      isCountry: true
                    };
                    handleRegionClick(countryRegion);
                  }
                } else {
                // In regions mode, select the specific region
                  const postalMapping = getPostalCodeMapping(countryCode);
                  const postalCode = postalMapping[nutsName];

                  if (nutsName && postalCode && countryCode) {
                    const region: PostalCodeRegion = {
                      id: feature.properties?.NUTS_ID || nutsName,
                      name: nutsName,
                      postalCodePrefix: postalCode,
                      country: countryCode
                    };
                    handleRegionClick(region);
                  }
                }
              }
            }}
            onLoad={(event: any) => {
              setMapLoaded(true);
              setMapRef(event.target);

              // Add postal code labels after style is loaded
              const map = event.target;
              setTimeout(() => {
                if (map.isStyleLoaded()) {
                  try {
                    // Find the existing NUTS layer
                    const layers = map.getStyle().layers;
                    const nutsLayer = layers.find((l: any) => l.id.includes('nuts'));

                    if (nutsLayer) {
                      addPostalCodeLabels(map);
                      addVisualLayers(map, nutsLayer);
                    }
                  } catch (error) {
                    console.error('Error adding postal code labels:', error);
                  }
                }
              }, 1000);
            }}
            onError={(_) => {
              // Suppress ALL Mapbox-related errors during development
              // These are mostly tile loading issues that don't affect functionality
              // Ignore ALL errors for now
            }}
          >
            {/* Your Mapbox style already contains the NUTS data */}
            <NavigationControl position="top-right" />
          </Map>
          {!mapLoaded && (
            <Box sx={styles.loadingBox}>
              <Typography>{t('Loading map...')}</Typography>
            </Box>
          )}
        </Box>

        {/* Selection Summary */}
        <Box sx={styles.selection}>
          <Typography variant="subtitle2" sx={styles.selectionTitle}>
            {t('Selected Regions:')}
          </Typography>

          <Box sx={styles.selectionBox}>
            <Typography variant="body2" sx={styles.loadingTitle}>
              {t('Loading')} ({selectedLoading.length}):
            </Typography>
            {selectedLoading.map(area => (
              <Chip
                key={area.id}
                label={area.isCountry ? `🌍 ${area.name}` : `${area.name} (${area.postalCodePrefix})`}
                size="small"
                sx={{ 
                  ...styles.chip, 
                  bgcolor: area.isCountry ? '#FFF3E0' : 'border.blueLight', 
                  color: area.isCountry ? '#F57C00' : 'border.blue',
                  fontWeight: area.isCountry ? 600 : 'normal'
                }}
                onDelete={() => setSelectedLoading(prev => prev.filter(a => a.id !== area.id))}
              />
            ))}
          </Box>

          <Box>
            <Typography variant="body2" sx={styles.deliveryTitle}>
              Delivery ({selectedDelivery.length}):
            </Typography>
            {selectedDelivery.map(area => (
              <Chip
                key={area.id}
                label={area.isCountry ? `🌍 ${area.name}` : `${area.name} (${area.postalCodePrefix})`}
                size="small"
                sx={{ 
                  ...styles.chip, 
                  bgcolor: area.isCountry ? '#FFF3E0' : 'border.green', 
                  color: area.isCountry ? '#F57C00' : '#388E3C',
                  fontWeight: area.isCountry ? 600 : 'normal'
                }}
                onDelete={() => setSelectedDelivery(prev => prev.filter(a => a.id !== area.id))}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Box>
          <Button onClick={handleClear} color="secondary" size="small">
            {t('Clear All')}
          </Button>
        </Box>

        <Box sx={styles.actionsButtons}>
          <Button onClick={onClose} variant="outlined">
            {t('Cancel')}
          </Button>
          <Button 
            onClick={handleSearch} 
            variant="contained"
            disabled={selectedLoading.length === 0 || selectedDelivery.length === 0}
          >
            {t('Create Search')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default InteractiveMapSearch;
